import { Injectable } from '@angular/core';
import { EMPTY, expand, map, Observable, of, scan, switchMap } from 'rxjs';
import { HttpService, interceptorsToken, WebinarDTO } from 'library-explorer';
import { CourseDTO } from 'src/app/model/courseDTO.model';
import { HttpParams } from '@angular/common/http';
import { ModuleDTO } from '@app/model/moduleDTO.model';
import { DataResponse } from '@app/model/data-response.model';

@Injectable({
  providedIn: 'root'
})
export class CourseService {

  constructor(
    private httpService: HttpService
  ) { }
  
  public getCourses(classID: string = null, extraParams?: HttpParams): Observable<DataResponse<CourseDTO>> {
    let params = extraParams || new HttpParams();

    params = params
      .set('active', 'true')
      .set(interceptorsToken.HANDLE_PERMISSION_DENIED_PARAM, 'true');

    if (classID) {
      params = params.set('parentClass', classID);
    }

    return this.httpService.get<DataResponse<CourseDTO>>('courses', params);
  }
  
  public getAllCourses(
    classId: string = null,
    extraParams: HttpParams = null,
    courses: CourseDTO[] = [],
    pagination: any = { itemsPerPage: 10, currentPage: 1, offset: 0 }
  ): Observable<CourseDTO[]> {
    let params = extraParams || new HttpParams();
    params = params
      .set('limit', pagination.itemsPerPage)
      .set('offset', pagination.offset);
    
    return this.getCourses(classId, params).pipe(
      expand(res => {
        const loaded = pagination.currentPage * pagination.itemsPerPage;

        if (res._meta.maxcount <= loaded) {
          return EMPTY;
        }

        pagination.currentPage++;
        pagination.offset = (pagination.currentPage - 1) * pagination.itemsPerPage;

        const nextParams = (extraParams || new HttpParams())
          .set('limit', pagination.itemsPerPage)
          .set('offset', pagination.offset);

        return this.getCourses(classId, nextParams);
      }),
      scan((allCourses, res) => {
        return [...allCourses, ...res.items];
      }, [])
    );
  }

  public getCourseByID(courseID: string): Observable<CourseDTO> {
    const params = new HttpParams()
      .set('active', 'true')
      .set('id', courseID)
      .set(interceptorsToken.HANDLE_PERMISSION_DENIED_PARAM, 'true');

    return this.httpService.get<CourseDTO>('node', params);
  }

  public getCourseDetailInfo(id: string): Observable<CourseDTO> {
    return this.httpService.get<CourseDTO>(`course/${id}/detail-info`);
  }

  public getCourseModulesList(id: string, offset = 0, limit = 20): Observable<DataResponse<ModuleDTO>> {
    const params = new HttpParams()
      .set('offset', offset.toString())
      .set('limit', limit.toString());

    return this.httpService.get<DataResponse<ModuleDTO>>(`course/${id}/modules`, params);
  }

  public getCourseWebinarsList(id: string, offset = 0, limit = 20): Observable<DataResponse<WebinarDTO>> {
    const params = new HttpParams()
      .set('offset', offset.toString())
      .set('limit', limit.toString());

    return this.httpService.get<DataResponse<WebinarDTO>>(`course/${id}/webinars`, params);
  }
}
